<template>
	<v-expansion-panel>
		<v-expansion-panel-title class="text-h6">
			<v-icon start>mdi-key</v-icon>

			API Tokens
		</v-expansion-panel-title>

		<v-expansion-panel-text>
			<v-card class="mb-4">
				<v-card-title class="d-flex justify-end">
					<v-spacer />
					<v-btn
						class="bg-secondary"
						:disabled="loading"
						@click="showCreateDialog = true"
					>
						<v-icon start>mdi-key-plus</v-icon>
						Create Token
					</v-btn>
				</v-card-title>

				<v-card-text>
					<div v-if="loading" class="text-center py-4">
						<v-progress-circular indeterminate />
					</div>

					<div v-else-if="tokens.length === 0" class="text-center py-4">
						<v-icon size="48">mdi-key-outline</v-icon>
						<p class="mt-2">No API tokens found</p>
						<p class="text-caption">
							Create an API token to access the Dactyl API
						</p>
					</div>

					<v-list v-else selectable>
						<v-list-item v-for="token in tokens" :key="token.id" class="mb-2">
							<template #prepend>
								<v-icon color="primary">mdi-key</v-icon>
							</template>

							<v-list-item-title>
								{{ token.name }}
							</v-list-item-title>
							<v-list-item-subtitle>
								Created:
								{{ formatDate(token.createdAt) }}
							</v-list-item-subtitle>

							<template #append>
								<v-list-item-action end>
									<v-btn
										color="error"
										size="small"
										:loading="revokingToken === token.id"
										@click="openRevokeDialog(token.id)"
									>
										<v-icon start>mdi-key-minus</v-icon>

										Revoke Token
									</v-btn>
								</v-list-item-action>
							</template>
						</v-list-item>
					</v-list>
				</v-card-text>
			</v-card>
		</v-expansion-panel-text>
	</v-expansion-panel>

	<v-dialog v-model="showCreateDialog" max-width="500">
		<v-card class="pb-5">
			<v-toolbar color="primary" dark flat>
				<v-card-title>
					<v-icon class="mr-2">mdi-key-plus</v-icon>
					Create API Token
				</v-card-title>
			</v-toolbar>

			<v-card-text>
				<v-form ref="form" @submit.prevent="createToken">
					<v-text-field
						v-model="newTokenName"
						label="Token Name"
						placeholder="e.g., My API Token"
						prepend-icon="mdi-key-plus"
						:rules="[(v) => !!v || 'Token name is required']"
						required
					/>
				</v-form>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn @click="showCreateDialog = false">Cancel</v-btn>
				<v-btn
					class="bg-secondary"
					:loading="creatingToken"
					:disabled="!newTokenName"
					@click="createToken"
				>
					Create Token
				</v-btn>
				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="showTokenDialog" max-width="600">
		<v-card class="pb-5">
			<v-toolbar color="primary" dark flat>
				<v-card-title>
					<v-icon class="mr-2">mdi-key</v-icon>
					API Token Created
				</v-card-title>
			</v-toolbar>

			<v-card-text>
				<div class="text-center mb-4">
					<v-icon size="64" color="success"> mdi-check-circle </v-icon>
					<h3 class="mt-2">Token created successfully!</h3>
					<p>Please copy your token now. You won't be able to see it again.</p>
				</div>

				<v-alert
					icon="mdi-information"
					class="mb-4 bg-valence-info-tint text-valence-info-ink"
				>
					<strong>Important:</strong> Store this token securely. It provides
					access to your account.
				</v-alert>

				<v-text-field
					:model-value="newToken"
					label="Your API Token"
					readonly
					variant="outlined"
					class="mb-2"
				>
					<template #append>
						<v-btn
							icon
							:color="copied ? 'success' : 'primary'"
							@click="copyToken"
						>
							<v-icon>
								{{ copied ? "mdi-check" : "mdi-content-copy" }}
							</v-icon>
						</v-btn>
					</template>
				</v-text-field>

				<div class="text-caption ml-2">
					Use this token in the Authorization header. It must be formatted as:
					<code>Authorization: Bearer YOUR_TOKEN</code>
				</div>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn class="bg-secondary" @click="showTokenDialog = false">
					Done
				</v-btn>
				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="showRevokeDialog" max-width="400">
		<v-card class="pb-5">
			<v-toolbar color="primary" dark flat>
				<v-card-title class="d-flex align-center">
					<v-icon class="mr-2"> mdi-alert-circle </v-icon>
					Revoke API Token
				</v-card-title>
			</v-toolbar>

			<v-card-text>
				<p>Are you sure you want to revoke this API token?</p>
				<p class="text-caption text-error mt-2">
					This action cannot be undone.
				</p>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn @click="showRevokeDialog = false">Cancel</v-btn>
				<v-btn
					class="bg-error text-white"
					:loading="revokingToken === tokenToRevoke"
					@click="confirmRevokeToken"
				>
					Revoke Token
				</v-btn>
				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

export default {
	name: "APITokenManager",

	data() {
		return {
			tokens: [],
			loading: false,
			creatingToken: false,
			revokingToken: null,
			showCreateDialog: false,
			showTokenDialog: false,
			showRevokeDialog: false,
			newTokenName: "",
			newToken: "",
			copied: false,
			tokenToRevoke: null,
		};
	},

	mounted() {
		this.loadTokens();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		loadTokens() {
			this.loading = true;
			this.$http
				.get("/api-tokens")
				.then((response) => {
					this.tokens = response.data.tokens;
				})
				.catch((error) => {
					console.error("Failed to load API tokens:", error);
					this.showNotification({
						message: "Failed to load API tokens",
						status: "error",
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},

		createToken() {
			if (!this.newTokenName.trim()) {
				return;
			}

			this.creatingToken = true;
			this.$http
				.post("/api-tokens", {
					name: this.newTokenName.trim(),
				})
				.then((response) => {
					this.newToken = response.data.token.token;
					this.showCreateDialog = false;
					this.showTokenDialog = true;
					this.newTokenName = "";

					// refresh the token list
					this.loadTokens();

					this.showNotification({
						message: "API token created successfully",
						status: "success",
					});
				})
				.catch((error) => {
					console.error("Failed to create API token:", error);
					this.showNotification({
						message:
							error.response?.data?.message || "Failed to create API token",
						status: "error",
					});
				})
				.finally(() => {
					this.creatingToken = false;
				});
		},

		openRevokeDialog(tokenId) {
			this.tokenToRevoke = tokenId;
			this.showRevokeDialog = true;
		},

		confirmRevokeToken() {
			if (!this.tokenToRevoke) {
				return;
			}

			this.revokingToken = this.tokenToRevoke;
			this.$http
				.delete(`/api-tokens/${this.tokenToRevoke}`)
				.then(() => {
					// remove the token from the list
					this.tokens = this.tokens.filter(
						(token) => token.id !== this.tokenToRevoke,
					);

					this.showRevokeDialog = false;
					this.tokenToRevoke = null;

					this.showNotification({
						message: "API token revoked successfully",
						status: "success",
					});
				})
				.catch((error) => {
					console.error("Failed to revoke API token:", error);
					this.showNotification({
						message:
							error.response?.data?.message || "Failed to revoke API token",
						status: "error",
					});
				})
				.finally(() => {
					this.revokingToken = null;
				});
		},

		async copyToken() {
			try {
				await navigator.clipboard.writeText(this.newToken);
				this.copied = true;
				setTimeout(() => {
					this.copied = false;
				}, 2000);
			} catch (error) {
				console.error("Failed to copy token:", error);
			}
		},

		formatDate(dateString) {
			return new Date(dateString).toLocaleDateString();
		},
	},
};
</script>

<style scoped>
code {
	background-color: #f5f5f5;
	padding: 2px 4px;
	border-radius: 4px;
	font-family: monospace;
}
</style>
